import { useEffect, useState } from 'react';
import { Link, NavLink, useSearchParams } from 'react-router';
import { api } from '../../shared/api/client';
import { EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { HINTS, useNavGroups } from '../shell/nav';

type Hit = { kind: string; title: string; subtitle: string | null; link: string };
const KIND: Record<string, string> = {
  menu: 'Menú', biblia: 'Biblia', persona: 'Personas', ministerio: 'Ministerios', agenda: 'Agenda', publicacion: 'Publicaciones', canto: 'Cantos',
  producto: 'Productos', recaudacion: 'Recaudaciones', salon: 'Salones', visitante: 'Visitantes',
};
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/** Otras palabras con las que la gente busca cada opción. */
const SYNONYMS: Record<string, string[]> = {
  '/app/finanzas': ['diezmo', 'diezmos', 'ofrenda', 'ofrendas', 'dinero', 'gastos', 'tesoreria', 'donativo'],
  '/app/promesas': ['promesa', 'pacto', 'compromiso', 'ofrenda'],
  '/app/ensayos': ['ensayo', 'ensayar', 'alabanza', 'musicos', 'coro'],
  '/app/guarderia': ['niños', 'ninos', 'bebes', 'bebe', 'cuna', 'infantes'],
  '/app/cultos': ['servicio', 'reunion dominical', 'domingo', 'programa'],
  '/app/salones': ['clases', 'escuela', 'discipulado', 'maestro', 'alumno'],
  '/app/salones/panel': ['maestro', 'clase', 'asistencia', 'alumnos'],
  '/app/calendario': ['eventos', 'fechas', 'agenda', 'reuniones'],
  '/app/oracion': ['peticion', 'orar', 'rezar'],
  '/app/oracion/diario': ['peticiones', 'respuestas', 'diario'],
  '/app/consejeria': ['pastor', 'consejo', 'cita', 'acompañamiento', 'problema'],
  '/app/ordenanzas': ['bautizo', 'bautismo', 'bautizarme', 'santa cena'],
  '/app/ordenanzas?ver=presentaciones': ['presentar', 'hijo', 'bebe', 'dedicacion'],
  '/app/mantenimiento': ['reparacion', 'arreglo', 'templo', 'falla'],
  '/app/comercio': ['cafeteria', 'cafe', 'tienda', 'libreria', 'vender', 'caja', 'comercio'],
  '/app/monedero': ['saldo', 'recarga', 'cafeteria'],
  '/app/recaudaciones': ['colecta', 'venta', 'kermes', 'causa', 'recaudar'],
  '/app/presentaciones': ['proyector', 'pantalla', 'presentaciones', 'diapositivas'],
  '/app/cantos': ['alabanza', 'himnos', 'letras', 'acordes', 'musica'],
  '/app/multimedia': ['fotos', 'videos', 'archivos'],
  '/app/miembros': ['personas', 'directorio', 'hermanos', 'familias', 'visitas'],
  '/app/ministerios': ['departamentos', 'lideres'],
  '/app/organigrama': ['estructura', 'pastoral', 'nombramientos', 'lideres'],
  '/app/avisos': ['anuncios', 'noticias', 'comunicados'],
  '/app/testimonios': ['gratitud', 'agradecimiento', 'gracias', 'testimonio'],
  '/app/hogar': ['familia', 'casa', 'hogar', 'hijos'],
  '/app/devocional': ['lectura', 'plan', 'devocional'],
  '/app/llamado': ['nuevo creyente', 'conocer a dios', 'salvacion', 'curso'],
  '/app/biblia/memorizar': ['memorizar', 'versiculos', 'memoria'],
  '/app/biblia/minutos': ['biblia en minutos', 'ideas del dia', 'historias', 'repaso', 'microaprendizaje'],
  '/app/biblia/mapa': ['mapa', 'lugares', 'geografia'],
  '/app/biblia/personajes': ['personajes', 'personas de la biblia'],
  '/app/biblia/diccionario': ['diccionario', 'significado', 'palabras'],
  '/app/biblia/cronologia': ['cronologia', 'linea del tiempo', 'historia'],
  '/app/biblia/temas': ['temas', 'versiculos por tema'],
  '/app/en-vivo': ['transmision', 'youtube', 'facebook', 'directo'],
  '/app/facturacion': ['pago', 'plan', 'suscripcion', 'factura'],
  '/app/aprobaciones': ['aprobar', 'visto bueno', 'solicitudes'],
  '/app/notificaciones': ['notificaciones', 'alertas'],
};

/** Páginas que no tienen opción propia en el menú, con quién puede verlas. */
function extraPages(can: (p: string) => boolean): { to: string; label: string; hint: string }[] {
  return [
    { to: '/app/ensayos', label: 'Ensayos', hint: 'Ensayos de alabanza y quién va.', visible: can('worship.read') },
    { to: '/app/organigrama', label: 'Organigrama', hint: HINTS['/app/organigrama'] ?? '', visible: can('departments.read') },
    { to: '/app/salones/panel', label: 'Panel del maestro', hint: 'Si das clase en Raíces: la clase de hoy, asistencia y puntos.', visible: can('departments.read') },
    { to: '/app/biblia/memorizar', label: 'Memorizar', hint: 'Aprende versículos de memoria.', visible: true },
    { to: '/app/biblia/minutos', label: 'Biblia en minutos', hint: 'Ideas cortas del día e historias con repaso.', visible: true },
    { to: '/app/biblia/mapa', label: 'Mapa bíblico', hint: 'Los lugares de la Biblia.', visible: true },
    { to: '/app/biblia/personajes', label: 'Personajes', hint: 'Quién es quién en la Biblia.', visible: true },
    { to: '/app/biblia/diccionario', label: 'Diccionario bíblico', hint: 'Qué significa cada palabra.', visible: true },
    { to: '/app/biblia/cronologia', label: 'Cronología', hint: 'La historia de la Biblia en orden.', visible: true },
    { to: '/app/biblia/temas', label: 'Versículos por tema', hint: 'Lo que dice la Biblia sobre cada tema.', visible: true },
    { to: '/app/notificaciones', label: 'Notificaciones', hint: 'Lo que te ha llegado y qué recibir.', visible: true },
  ]
    .filter((p) => p.visible)
    .map(({ to, label, hint }) => ({ to, label, hint }));
}

/** ¿La opción coincide por su nombre, su descripción o una palabra parecida? */
function matches(q: string, label: string, to: string): boolean {
  const n = norm(q);
  return [label, HINTS[to] ?? '', ...(SYNONYMS[to] ?? [])].some((t) => norm(t).includes(n));
}

/** Botón de búsqueda de la barra superior. */
export function SearchButton() {
  return (
    <NavLink to="/app/buscar" className="bell" aria-label="Buscar en KEORA">
      <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="11" cy="11" r="6.5" />
        <path d="m16 16 4.5 4.5" />
      </svg>
    </NavLink>
  );
}

/** Buscador global: menú, personas, agenda, predicaciones, ministerios… según lo que puedo ver. */
export function SearchPage() {
  const me = useMe();
  const groups = useNavGroups();
  const [params, setParams] = useSearchParams();
  const [text, setText] = useState(params.get('q') ?? '');
  const q = params.get('q') ?? '';
  useEffect(() => {
    const t = setTimeout(() => {
      if (text.trim() !== q) setParams(text.trim() ? { q: text.trim() } : {}, { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [text, q, setParams]);
  const data = useAsync(
    (signal) => (me.activeChurchId && q.length >= 2 ? api.get<{ items: Hit[] }>(`/reporting/churches/${me.activeChurchId}/search?q=${encodeURIComponent(q)}`, signal).then((r) => r.items) : Promise.resolve([])),
    [me.activeChurchId, q],
  );
  const pages = [...groups.flatMap((g) => g.items).map((i) => ({ to: i.to, label: i.label, hint: HINTS[i.to] ?? '' })), ...extraPages(me.can)];
  const menu: Hit[] = q.length >= 2
    ? pages
        .filter((p, n) => pages.findIndex((x) => x.to === p.to) === n && matches(q, p.label, p.to))
        .map((p) => ({ kind: 'menu', title: p.label, subtitle: p.hint || null, link: p.to }))
    : [];
  const hits = [...menu, ...(data.status === 'ready' ? data.data : [])];
  const kinds = [...new Set(hits.map((h) => h.kind))];

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Buscar</h1>
      </header>
      <div className="field">
        <label htmlFor="global-search">¿Qué buscas?</label>
        <input id="global-search" type="search" autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Personas, eventos, predicaciones, cantos, Juan 3:16…" />
      </div>
      {q.length >= 2 && data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} /> : null}
      {q.length >= 2 && data.status === 'ready' && hits.length === 0 ? <EmptyState title="No encontré nada con eso." /> : null}
      {kinds.map((k) => (
        <section key={k} className="card">
          <h2 className="small muted">{KIND[k] ?? k}</h2>
          <ul className="list">
            {hits.filter((h) => h.kind === k).map((h, i) => (
              <li key={`${h.link}-${i}`} className="list__item">
                <Link className="list__link" to={h.link}>
                  <strong>{h.title}</strong>
                  {h.subtitle ? <span className="small muted">{k === 'agenda' ? new Date(h.subtitle).toLocaleString('es', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : h.subtitle}</span> : null}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
